import { useEffect, useState } from 'react';
import { NudosSearchbarDropdown } from '../../../../../../components/NudosComponents';
import BordOneToneIcon from '../../../../../../components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import useStockOrdersModuleState from '../../../../../../state/useStockOrdersModuleState';
import { CREAT_NEW_ORDER_STOCK_STEPS } from '../../../../../../types/orders';
import { countriesList } from '../../../../../../services/countriesList';
import { ICountryDetail } from '../../../../../../types/countries';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './SelectWarehouse.scss';


const useCountriesList = () => {
  const [countries, setCountries] = useState<ICountryDetail[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCountries = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await countriesList();
      setCountries(response);
    } catch (err) {
      displayErrorNotification(
        {
          customJSXMessage: <>Error al cargar la lista de países</>
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  return { countries, isLoading, error };
};

const SelectWarehouse = () => {
  const { searchWarehouse, setSearchWarehouse, setCurrentStep, setSelectedCountryCode, setSelectedCountryId } = useStockOrdersModuleState();
  const { countries, isLoading, error } = useCountriesList();
  const filteredCountries = countries.filter(country => country?.storage);

  const selectedCountry = searchWarehouse
    ? filteredCountries.find(c => c?.id?.toString() === searchWarehouse?.id?.toString())
    : undefined;

  const handleCountrySelect = (country: ICountryDetail) => {
    setSearchWarehouse({
      name: country?.name || '',
      id: country?.id || 0,
      flag: country?.flag || '',
      currencyCode: country?.currencyCode || ''
    });
  };

  const renderSelectedCountry = (country: ICountryDetail) => (
    <div className="selectedWarehouse" onClick={() => handleCountrySelect(country)}>
      <span className="warehouseName">{country.name}</span>
    </div>
  );

  const renderCountryOptions = (
    countries: ICountryDetail[],
    onSelect?: (country: ICountryDetail) => void,
    searchString?: string
  ) => {
    return countries.map(country => (
      <div
        key={country.id}
        className="optionContainer"
        onClick={() => {
          setSelectedCountryCode(country?.code || null);
          setSelectedCountryId(country?.id || null);
          onSelect?.(country);
          setSearchWarehouse({
            name: country?.name || '',
            id: country?.id || 0,
            flag: country?.flag || '',
            currencyCode: country?.currencyCode || ''
          });
        }}
      >
        <div className="warehouseOption">
          <div className="warehouseName">{`Bodega ${country?.name}`}</div>
        </div>
      </div>
    ));
  };

  const filterCountries = (search: string, countries: ICountryDetail[]) => {
    const searchLower = search.toLowerCase();
    return countries.filter(country => country?.name?.toLowerCase().includes(searchLower));
  };

  useEffect(() => {
    if (selectedCountry) {
      setCurrentStep(CREAT_NEW_ORDER_STOCK_STEPS.SELECT_PRODUCTS);
    }
  }, [selectedCountry, setSearchWarehouse, setCurrentStep]);

  if (error) {
    return (
      <div className="selectWarehouse">
        <div className="selectWarehouseContainer">
          <div className="error">Error al cargar los países. Por favor, intenta de nuevo.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="selectWarehouse">
      <div className="selectWarehouseContainer">
        <div className="warehouseIcon">
          <BordOneToneIcon variant="stockOrder" standardSize={24} />
        </div>
        <div className="title">Selecciona una Bodega</div>
        <div className="description">Elige la bodega donde realizarás la gestión de inventario</div>
        <NudosSearchbarDropdown
          label="Bodega donde va el stock"
          componentSize="large"
          formSelectedOptionUICallback={renderSelectedCountry}
          formDropdownOptionsCallback={renderCountryOptions}
          filterBySearchStringCallback={filterCountries}
          customPlaceholder={isLoading ? 'Cargando países...' : 'Busca y selecciona el país'}
          currentValue={selectedCountry}
          isFilled={!!selectedCountry}
          rawOptions={filteredCountries}
          dropdownCloseCallback={() => {}}
        />
      </div>
    </div>
  );
};

export default SelectWarehouse;
